import { jsPDF as JsPDF } from "jspdf";
import moment from "moment";

/**
 * Individual Development Plan as a printable A4 PDF — one IDP review
 * (four pillars + comments) with the player's goals and the quarter's game
 * targets. Built for players to hand to performance schools and for coaches
 * to print for review meetings, so it's vector text (selectable, small),
 * not a screenshot.
 */

const PAGE = { w: 210, h: 297, margin: 14 };
const INK = {
  dark: [17, 24, 39],
  mid: [100, 116, 139],
  light: [226, 232, 240],
  pale: [248, 250, 252],
  white: [255, 255, 255],
  brand: [136, 163, 136], // matches the dashboard header
};
const BAND = {
  green: { label: "Excelling", rgb: [34, 197, 94] },
  yellow: { label: "Coping", rgb: [245, 158, 11] },
  red: { label: "Developing", rgb: [239, 68, 68] },
};
// Column names are the DB's (incl. the "physcological" misspelling).
const PILLARS = [
  { label: "Technical", prefix: "technical" },
  { label: "Tactical", prefix: "tactical" },
  { label: "Physical", prefix: "physical" },
  { label: "Mental", prefix: "physcological" },
];

const fmtDate = (d) => (d ? moment(d).format("D MMMM YYYY") : "");
const text = (v) => String(v ?? "").trim();

/**
 * @param {object} opts
 *   clubName, player{name, teamName, position}, report (player_idp_scores row),
 *   goals[] ({goal, how_can, what_do_i_need, month_category}),
 *   targets[] ({target, complete, possession}), quarterLabel
 */
export function buildIdpPdf(opts) {
  const {
    clubName = "",
    player = {},
    report = {},
    goals = [],
    targets = [],
    quarterLabel = "",
  } = opts;

  const doc = new JsPDF({ unit: "mm", format: "a4" });
  const contentW = PAGE.w - PAGE.margin * 2;
  let y = 0;

  // ---- page furniture ------------------------------------------------------
  const footer = () => {
    const n = doc.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...INK.mid);
      doc.text(
        `${clubName} · Individual Development Plan · ${text(player.name)}`,
        PAGE.margin,
        PAGE.h - 8,
      );
      doc.text(`${i} / ${n}`, PAGE.w - PAGE.margin, PAGE.h - 8, { align: "right" });
    }
  };

  // Start a new page when `need` mm won't fit above the footer.
  const ensure = (need) => {
    if (y + need > PAGE.h - 16) {
      doc.addPage();
      y = PAGE.margin;
    }
  };

  const sectionTitle = (title) => {
    // Title + at least a couple of lines of content, so a heading never
    // sits alone at the foot of a page.
    ensure(30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5);
    doc.setTextColor(...INK.dark);
    doc.text(title, PAGE.margin, y + 5);
    doc.setDrawColor(...INK.light);
    doc.setLineWidth(0.4);
    doc.line(PAGE.margin, y + 7.5, PAGE.w - PAGE.margin, y + 7.5);
    y += 12;
  };

  // Wrapped paragraph; returns height used.
  const paragraph = (str, x, width, size = 9, colour = INK.dark, lineH = 4.3) => {
    const lines = doc.splitTextToSize(text(str) || "—", width);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(size);
    doc.setTextColor(...colour);
    doc.text(lines, x, y);
    return lines.length * lineH;
  };

  // ---- header -------------------------------------------------------------
  doc.setFillColor(...INK.brand);
  doc.rect(0, 0, PAGE.w, 34, "F");
  doc.setTextColor(...INK.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Individual Development Plan", PAGE.margin, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text(clubName, PAGE.margin, 20);
  const who = [text(player.name), text(player.teamName), text(player.position)]
    .filter(Boolean)
    .join("  ·  ");
  doc.setFontSize(10.5);
  doc.setFont("helvetica", "bold");
  doc.text(who, PAGE.margin, 28);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const right = PAGE.w - PAGE.margin;
  doc.text(`Review date: ${fmtDate(report.created_date)}`, right, 13, { align: "right" });
  if (quarterLabel) doc.text(quarterLabel, right, 19, { align: "right" });
  doc.text(
    `Generated ${moment().format("D MMM YYYY")}`,
    right,
    25,
    { align: "right" },
  );
  y = 42;

  // ---- pillars 2×2 ----------------------------------------------------------
  sectionTitle("Skill areas");
  const gap = 5;
  const boxW = (contentW - gap) / 2;
  const innerW = boxW - 8;

  // Measure each pillar so the two boxes in a row share a height.
  const pillarLines = PILLARS.map((p) =>
    ["green", "yellow", "red"].map((band) => {
      doc.setFontSize(8.5);
      return doc.splitTextToSize(text(report[`${p.prefix}_${band}`]) || "—", innerW - 4);
    }),
  );
  const pillarH = (lines) => 10 + lines.reduce((h, l) => h + 4.5 + l.length * 3.9 + 1.5, 0) + 2;

  for (let row = 0; row < 2; row++) {
    const a = row * 2;
    const h = Math.max(pillarH(pillarLines[a]), pillarH(pillarLines[a + 1]));
    ensure(h + 4);
    [0, 1].forEach((col) => {
      const p = PILLARS[a + col];
      const lines = pillarLines[a + col];
      const x = PAGE.margin + col * (boxW + gap);
      doc.setFillColor(...INK.pale);
      doc.setDrawColor(...INK.light);
      doc.roundedRect(x, y, boxW, h, 2, 2, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...INK.dark);
      doc.text(p.label, x + 4, y + 6.5);
      let cy = y + 12;
      ["green", "yellow", "red"].forEach((band, i) => {
        doc.setFillColor(...BAND[band].rgb);
        doc.circle(x + 5.5, cy - 1, 1.3, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(...INK.mid);
        doc.text(BAND[band].label.toUpperCase(), x + 8.5, cy);
        cy += 4.5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...INK.dark);
        doc.text(lines[i], x + 8.5, cy);
        cy += lines[i].length * 3.9 + 1.5;
      });
    });
    y += h + 4;
  }
  y += 2;

  // ---- comments -----------------------------------------------------------
  sectionTitle("Comments");
  [
    ["Player", report.player_comments],
    ["Coach", report.coach_comments],
  ].forEach(([who, body]) => {
    doc.setFontSize(9);
    const lines = doc.splitTextToSize(text(body) || "No comments added.", contentW - 8);
    const h = 9 + lines.length * 4.3;
    ensure(h + 3);
    doc.setFillColor(...INK.pale);
    doc.setDrawColor(...INK.light);
    doc.roundedRect(PAGE.margin, y, contentW, h, 2, 2, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...INK.mid);
    doc.text(`${who.toUpperCase()} COMMENTS`, PAGE.margin + 4, y + 5);
    y += 9;
    paragraph(lines.join("\n"), PAGE.margin + 4, contentW - 8, 9);
    y += lines.length * 4.3 + 3;
  });
  y += 2;

  // ---- goals --------------------------------------------------------------
  sectionTitle("Goals");
  const horizons = [
    ["12", "12-month goals"],
    ["6", "6-month goals"],
    ["3", "3-month goals"],
  ];
  let anyGoal = false;
  horizons.forEach(([cat, label]) => {
    const list = goals.filter((g) => String(g.month_category) === cat);
    if (!list.length) return;
    anyGoal = true;
    ensure(10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...INK.brand);
    doc.text(label, PAGE.margin, y + 4);
    y += 8;
    list.forEach((g) => {
      doc.setFontSize(9);
      const goalLines = doc.splitTextToSize(text(g.goal) || "—", contentW - 6);
      const detail = [
        g.how_can ? `How: ${text(g.how_can)}` : null,
        g.what_do_i_need ? `Needs: ${text(g.what_do_i_need)}` : null,
      ].filter(Boolean);
      doc.setFontSize(8.5);
      const detailLines = detail.flatMap((d) => doc.splitTextToSize(d, contentW - 10));
      const h = goalLines.length * 4.3 + detailLines.length * 4 + 4;
      ensure(h);
      doc.setFillColor(...INK.brand);
      doc.circle(PAGE.margin + 1.5, y - 1.2, 1, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...INK.dark);
      doc.text(goalLines, PAGE.margin + 5, y);
      y += goalLines.length * 4.3;
      if (detailLines.length) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...INK.mid);
        doc.text(detailLines, PAGE.margin + 5, y);
        y += detailLines.length * 4;
      }
      y += 3;
    });
  });
  if (!anyGoal) {
    ensure(8);
    paragraph("No goals set yet.", PAGE.margin, contentW, 9, INK.mid);
    y += 8;
  }
  y += 2;

  // ---- game targets ---------------------------------------------------------
  sectionTitle(quarterLabel ? `Game targets — ${quarterLabel}` : "Game targets");
  const buckets = [
    ["In possession", targets.filter((t) => t.possession === "In possession")],
    ["Out of possession", targets.filter((t) => t.possession === "Out of possession")],
  ];
  const other = targets.filter(
    (t) => t.possession !== "In possession" && t.possession !== "Out of possession",
  );
  if (other.length) buckets.push(["Other", other]);
  if (!targets.length) {
    ensure(8);
    paragraph("No game targets set for this period.", PAGE.margin, contentW, 9, INK.mid);
    y += 8;
  }
  buckets.forEach(([label, list]) => {
    if (!list.length) return;
    ensure(10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...INK.brand);
    doc.text(label, PAGE.margin, y + 4);
    y += 8;
    list.forEach((t) => {
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(text(t.target) || "—", contentW - 24);
      const h = lines.length * 4.3 + 2.5;
      ensure(h);
      const done = Number(t.complete) === 1;
      // Status box: filled green tick box when complete, empty when pending.
      doc.setDrawColor(...(done ? BAND.green.rgb : INK.mid));
      doc.setFillColor(...(done ? BAND.green.rgb : INK.white));
      doc.roundedRect(PAGE.margin, y - 3.2, 3.6, 3.6, 0.6, 0.6, "FD");
      if (done) {
        doc.setDrawColor(...INK.white);
        doc.setLineWidth(0.5);
        doc.line(PAGE.margin + 0.8, y - 1.4, PAGE.margin + 1.6, y - 0.6);
        doc.line(PAGE.margin + 1.6, y - 0.6, PAGE.margin + 3, y - 2.6);
        doc.setLineWidth(0.4);
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...INK.dark);
      doc.text(lines, PAGE.margin + 6, y);
      doc.setFontSize(7.5);
      doc.setTextColor(...(done ? BAND.green.rgb : INK.mid));
      doc.text(done ? "COMPLETE" : "IN PROGRESS", PAGE.w - PAGE.margin, y, { align: "right" });
      y += h;
    });
    y += 2;
  });

  footer();
  return doc;
}

/** Build + trigger the browser download. */
export function downloadIdpPdf(opts) {
  const doc = buildIdpPdf(opts);
  const who = text(opts.player?.name).replace(/[^\w]+/g, "-") || "player";
  const when = opts.report?.created_date
    ? moment(opts.report.created_date).format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");
  doc.save(`IDP-${who}-${when}.pdf`);
  return doc;
}
